// Supervision du parc (SA-9.1) — savoir en deux secondes quel magasin a un problème, et lesquels vont bien.
//
// ⚠️ JAMAIS DE CHIFFRE D'AFFAIRES (brief SA-9.1). L'éditeur voit si ça fonctionne, pas si ça vend —
// aucun montant ne transite par cet écran, et la requête serveur ne touche que les curseurs de sync.
//
// ⚠️ Le battement vient du BO, pas de la Console. `device.last_seen_at` n'est écrit qu'à la mise en
// service : un écran bâti dessus aurait affiché « vu il y a 3 jours » pour une caisse éteinte depuis
// un mois. Ici la date vient de `sync_device_cursor.seen_at`, écrit à CHAQUE pull.
//
// ⚠️ L'ÉTAT D'UN MAGASIN EST CELUI DE SON APPAREIL LE PLUS MUET, calculé par `storeHealth` — le même calcul que la fiche
//    magasin, la liste des magasins et « À traiter ». Le juger sur le contact le plus récent cachait une caisse morte
//    derrière sa voisine en ligne.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type RapportOrphelins, type SupervisionStore } from '../api';
import { EmptyState } from '@orgazone/ui-web';
import { Chip, Pill } from '../ui';
import { dateTimeShort } from '@orgazone/date-format';
import { DataTable, type DtRow } from '../data-table';
import {
  HEALTH_ORDER, healthTone, OFFLINE_AFTER_H, SILENT_AFTER_H, storeHealth, timeAgo, type StoreHealth, type SyncKind,
} from '../sync-health';

/**
 * Cadence du relèvement automatique.
 *
 * ⚠️ QUINZE MINUTES, ET LE CHIFFRE SE DÉDUIT DES SEUILS : cet écran juge « silencieux après 24 h » et « hors ligne après
 * 72 h ». Rappeler toutes les minutes ne pouvait RIEN changer à un verdict. Le serveur partage d'ailleurs sa tournée
 * quelques minutes entre ses lecteurs — c'est l'âge de CETTE tournée qui s'affiche, jamais l'heure de l'écran.
 */
const RAFRAICHISSEMENT_MS = 900_000;

/** Les quatre états, dans l'ordre de la bande — exclusifs, chacun un filtre. */
const STATES: readonly { k: Exclude<SyncKind, 'na'>; label: string; sub: string }[] = [
  { k: 'da', label: 'Hors ligne', sub: `plus de ${OFFLINE_AFTER_H} h sans contact` },
  { k: 'wa', label: 'Silencieux', sub: `muets depuis ${SILENT_AFTER_H} à ${OFFLINE_AFTER_H} h` },
  { k: 'ne', label: 'Jamais connectés', sub: 'aucun appareil n’a encore synchronisé' },
  { k: 'ok', label: 'En ligne', sub: `tous les appareils ont parlé depuis moins de ${SILENT_AFTER_H} h` },
];

type Filter = 'all' | Exclude<SyncKind, 'na'> | 'update' | 'outside';

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

export function Supervision() {
  const nav = useNavigate();
  const [rows, setRows] = useState<SupervisionStore[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  /** L'instant de la tournée affichée — celui du SERVEUR, qui la partage entre ses lecteurs. */
  const [measuredAt, setMeasuredAt] = useState<string | null>(null);
  const [releveEnCours, setReleveEnCours] = useState(false);
  const relever = useRef<(fresh: boolean) => void>(() => {});
  /**
   * L'INVENTAIRE DES ORPHELINS — ce qui survit à un magasin que l'annuaire ne connaît plus.
   * ⚠️ CHARGÉ UNE FOIS : il interroge trois systèmes et son résultat ne bouge qu'à une suppression.
   */
  const [orph, setOrph] = useState<RapportOrphelins | null>(null);
  const [orphErr, setOrphErr] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    api.orphelins().then((r) => { if (vivant) setOrph(r); }).catch((e) => { if (vivant) setOrphErr((e as Error).message); });
    return () => { vivant = false; };
  }, []);

  useEffect(() => {
    let vivant = true;
    const read = (fresh: boolean) => {
      setReleveEnCours(true);
      return api.supervision(fresh)
        .then((r) => { if (vivant) { setRows(r.stores); setMeasuredAt(r.measuredAt); setErr(null); } })
        .catch((e) => { if (vivant) { setRows(null); setErr((e as Error).message); } })
        .finally(() => { if (vivant) setReleveEnCours(false); });
    };
    relever.current = (fresh) => { void read(fresh); };
    void read(false);
    const t = setInterval(() => void read(false), RAFRAICHISSEMENT_MS);
    return () => { vivant = false; clearInterval(t); };
  }, []);

  // Une santé par magasin, triée du plus grave au plus sain — puis, à état égal, ce qui porte un autre signal, puis le plus
  // ancien contact : on ouvre cet écran pour trouver ce qui ne va pas.
  const all = useMemo(() => {
    if (!rows) return [];
    const flags = (x: { r: SupervisionStore; h: StoreHealth }) => (x.h.late.length ? 1 : 0) + (x.r.knownInDirectory ? 0 : 1);
    return rows.map((r) => ({ r, h: storeHealth(r) }))
      .sort((a, b) => HEALTH_ORDER[a.h.kind] - HEALTH_ORDER[b.h.kind] || flags(b) - flags(a)
        || (b.h.worst?.hours ?? 0) - (a.h.worst?.hours ?? 0) || a.r.name.localeCompare(b.r.name));
  }, [rows]);
  const count = (k: SyncKind) => all.filter((x) => x.h.kind === k).length;
  const late = all.filter((x) => x.h.late.length).length;
  const outside = all.filter((x) => !x.r.knownInDirectory).length;
  const shown = all.filter((x) => filter === 'all' || x.h.kind === filter
    || (filter === 'update' && x.h.late.length > 0) || (filter === 'outside' && !x.r.knownInDirectory));

  const tableRows = shown.map(({ r, h }): DtRow => {
    const tone = healthTone(h.kind);
    const more = [h.unpaired ? plural(h.unpaired, 'jamais appairé', 'jamais appairés') : null, h.terminals.length ? `${h.terminals.length} Mobile Partner` : null].filter(Boolean);
    const depot = r.kind === 'depot';
    return {
      id: r.gln,
      // La ligne ouvre la fiche du magasin ; un magasin hors annuaire n'en a pas.
      onClick: r.storeId && r.tenantId ? () => nav(`/clients/${r.tenantId}/magasin/${r.storeId}`) : undefined,
      tint: h.kind === 'da' ? 'color-mix(in srgb, var(--da-fg) 6%, transparent)' : undefined,
      cells: {
        state: {
          v: <Pill tone={tone}>{h.label}</Pill>, sub: h.reason ?? undefined, subTone: h.kind === 'da' || h.kind === 'wa' ? tone : undefined,
          text: `${h.label} ${h.reason ?? ''}`, sortValue: HEALTH_ORDER[h.kind],
        },
        name: {
          v: <span style={{ fontWeight: 600 }}>{r.name}</span>,
          sub: r.knownInDirectory ? (r.tenantName ?? '—') : 'hors annuaire', subTone: r.knownInDirectory ? undefined : 'wa',
          sub2: `${r.sCode ? `S${r.sCode} · ` : ''}GLN ${r.gln}`,
          text: `${r.name} ${r.tenantName ?? ''} ${r.gln} ${[...h.tills, ...h.terminals].map((d) => d.code).join(' ')}`, sortValue: r.name,
        },
        contact: h.worst
          ? { v: <span className="mono">{timeAgo(new Date(h.worst.at).toISOString())}</span>, sub: `${h.worst.code} · ${dateTimeShort(new Date(h.worst.at).toISOString())}`, sortValue: -h.worst.hours }
          : { v: <span className="mono">{r.knownInDirectory ? '—' : 'jamais'}</span>, sub: r.error ?? undefined, sortValue: 1 },
        tills: {
          v: <span className="mono">{h.tills.length ? `${h.tillsOnline} / ${h.tills.length} en ligne` : depot ? 'dépôt' : 'aucune caisse'}</span>,
          sub: more.length ? `+ ${more.join(' · ')}` : undefined, sortValue: h.tills.length - h.tillsOnline,
        },
        // ⚠️ LA COULEUR NE SIGNALE QU'UN PROBLÈME : une valeur saine reste en texte gris, un retard devient une pastille.
        version: !h.tills.length ? { v: <span style={{ color: 'var(--ink500)' }}>—</span> }
          : !r.expectedVersion ? { v: <span style={{ color: 'var(--ink500)' }}>non jugée</span>, sub: r.versionSource }
          : h.late.length ? { v: <Pill tone="in">{`${h.late.length} à mettre à jour`}</Pill>, sub: `${h.late.map((d) => `${d.code} en ${d.version}`).join(' · ')} → ${r.expectedVersion}` }
          : { v: <span style={{ color: 'var(--ink500)' }}>à jour</span>, sub: `${r.versionSource} · ${r.expectedVersion}` },
        // ⚠️ LES PÉRIODES FISCALES ARRIVENT AVEC SA-9.2 : d'ici là, la colonne le DIT — jamais « à jour » sur ce qu'on ne mesure pas.
        period: { v: <span style={{ color: 'var(--ink500)' }}>{depot ? 'sans objet' : !r.knownInDirectory ? '—' : 'à venir'}</span> },
      },
    };
  });

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px 16px', marginBottom: 16 }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <h1 className="page-title">Supervision</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>
            Santé technique des magasins : le dernier contact de chaque appareil. Aucun chiffre d’affaires ne remonte ici.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          {/* ⚠️ L'ÂGE DE LA TOURNÉE, PAS LA CADENCE NI L'HEURE DE L'ÉCRAN : c'est d'elle que datent les verdicts. */}
          <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', whiteSpace: 'nowrap' }}>
            {releveEnCours ? 'relèvement en cours…' : measuredAt ? `relevé ${timeAgo(measuredAt)}` : 'jamais relevé'}
          </span>
          <button type="button" className="btn-ghost" disabled={releveEnCours} onClick={() => relever.current(true)}>
            {releveEnCours ? 'Relèvement…' : 'Relever maintenant'}
          </button>
        </div>
      </div>

      {err && <div className="err" style={{ marginBottom: 12 }}>Supervision indisponible : {err}</div>}
      {!rows && !err && <div className="muted" style={{ padding: 16 }}>Tournée du parc…</div>}

      {rows && rows.length === 0 && (
        <div className="dcard"><EmptyState icon="pulse" title="Aucun magasin déclaré"
          note="Le back-office ne connaît encore aucun magasin. Un magasin est déclaré à sa mise en service depuis la fiche client." /></div>
      )}

      {rows && rows.length > 0 && (
        <>
          {/* Les quatre états — exclusifs, chacun un filtre ; un second clic réaffiche tout. */}
          <div className="sup-band" role="group" aria-label="État des magasins">
            {STATES.map((s) => {
              const n = count(s.k);
              const on = filter === s.k;
              return (
                <button key={s.k} type="button" aria-pressed={on} className={['sup-state', s.k, n ? 'lit' : null, on ? 'on' : null].filter(Boolean).join(' ')}
                  onClick={() => setFilter(on ? 'all' : s.k)}>
                  <span className="lbl"><span className="dot" /><span className="k">{s.label}</span></span>
                  <span className="num">{n}</span>
                  <span className="sub">{s.sub}</span>
                </button>
              );
            })}
          </div>

          {/* Les autres signaux — ils se cumulent (un magasin peut être en ligne ET en retard de version) ; masqués à zéro. */}
          <div className="sup-flags">
            {(late > 0 || outside > 0) && (
              <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)', marginRight: 2 }}>À surveiller aussi</span>
            )}
            {late > 0 && <Chip on={filter === 'update'} onClick={() => setFilter(filter === 'update' ? 'all' : 'update')}>À mettre à jour<span className="n" style={{ color: 'var(--in-fg)' }}>{late}</span></Chip>}
            {outside > 0 && <Chip on={filter === 'outside'} onClick={() => setFilter(filter === 'outside' ? 'all' : 'outside')}>Hors annuaire<span className="n" style={{ color: 'var(--wa-fg)' }}>{outside}</span></Chip>}
            <div style={{ flex: 1 }} />
            {filter !== 'all' && (
              <button type="button" className="btn-link marque" onClick={() => setFilter('all')}>Tout afficher · {plural(all.length, 'magasin', 'magasins')}</button>
            )}
          </div>

          <DataTable
            cols={[
              { key: 'state', label: 'État', sortable: true, wrap: true },
              { key: 'name', label: 'Magasin', sortable: true },
              { key: 'contact', label: 'Plus ancien contact', sortable: true, wrap: true },
              { key: 'tills', label: 'Caisses', sortable: true, wrap: true },
              { key: 'version', label: 'Version', wrap: true },
              { key: 'period', label: 'Période fiscale', wrap: true },
            ]}
            rows={tableRows}
            unit="magasin"
            searchPlaceholder="Magasin, client, GLN, appareil…"
            pageSize={25}
            emptyIcon="pulse"
            emptyTitle="Aucun magasin dans ce filtre"
            emptyNote="Changez de filtre, ou affichez tout le parc."
          />
          <div className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 8, lineHeight: 1.6 }}>
            Seuils : silencieux après {SILENT_AFTER_H} h sans contact, hors ligne après {OFFLINE_AFTER_H} h. L’état d’un magasin est celui de son appareil le plus muet.
            Versions comparées au canal du magasin (flux public, ou groupe choisi dans sa fiche) ; un appareil servi par le store de son fabricant n’est pas jugé.
          </div>
        </>
      )}

      {/* ── CE QUI SURVIT À UN MAGASIN QUE L'ANNUAIRE NE CONNAÎT PLUS ─────────────────────────
          ⚠️ SOUS la liste des magasins (décision de l’utilisateur) : la maquette ne le dessine pas. ⚠️ L'écran NOMME, il ne nettoie pas : une base sans
             magasin porte des pièces fiscales scellées. Le retrait du registre et du site EDI passe par la suppression du
             magasin, dans Clients. */}
      <OrphelinsBloc rapport={orph} erreur={orphErr} />
    </div>
  );
}

/** Une ligne d'orphelin — son GLN, son nom, et CE QUE ÇA COÛTE de le laisser là. */
function LigneOrph({ o }: { o: { gln: string; nom: string | null; detail?: string } }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 14px', padding: '7px 0', borderTop: '1px solid var(--ink100)' }}>
      <code className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink700)', minWidth: 112 }}>{o.gln}</code>
      <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, minWidth: 150 }}>{o.nom ?? '—'}</span>
      {o.detail && <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', lineHeight: 1.45 }}>{o.detail}</span>}
    </div>
  );
}

/** Les groupes de l'inventaire, chacun avec CE QU'IL FAUT EN FAIRE et son verdict. */
export function orphanGroups(rapport: RapportOrphelins) {
  // ⚠️ CHAQUE GROUPE DIT CE QU'IL FAUT EN FAIRE : nommer un reliquat sans dire quoi en faire laisse le lecteur devant une
  //    anomalie qu'il ne peut pas traiter. Un écran de supervision distingue « à traiter » de « normal, ne touchez à rien ».
  return [
    { titre: 'Inscrits au back-office sans magasin', lignes: rapport.registre, grave: true, verdict: 'à retirer', tone: 'da',
      action: 'Le back-office connaît ces magasins, l’annuaire non : ils apparaissent « hors annuaire » dans le tableau. À retirer avec l’outil de nettoyage, sur le serveur de la Console.',
      cmd: 'node dist/orphelins.js --purger' },
    { titre: 'Sites EDI sans magasin', lignes: rapport.sitesEdi, grave: true, verdict: 'à retirer', tone: 'da',
      action: 'Même outil, même passage.', cmd: 'node dist/orphelins.js --purger' },
    { titre: 'Bases sans magasin', lignes: rapport.bases, grave: false, verdict: 'décision humaine', tone: 'wa',
      action: 'Elles portent des pièces fiscales scellées : à archiver, puis à détruire à la main, une fois. Aucun outil ne le fera pour vous.', cmd: null },
    { titre: 'Rôles sans magasin', lignes: rapport.roles, grave: false, verdict: 'rien à faire', tone: 'ne',
      action: 'Leur survie est voulue : les instantanés de migration leur appartiennent, et les détruire emporterait les seules copies de secours. Ils sont listés pour qu’on ne les prenne pas pour un oubli.', cmd: null },
  ].filter((g) => g.lignes.length > 0);
}

/**
 * L'INVENTAIRE DES ORPHELINS, replié en une ligne qui porte son bilan.
 *
 * ⚠️ UN RAPPORT VIDE NE SUFFIT PAS À RASSURER : « rien à nettoyer » et « je n'ai pas pu regarder » s'écrivent de la même
 * façon. Les sources non vérifiées s'affichent donc TOUJOURS, avant le verdict, et « rien à retirer » ne se rend que si la
 * liste des non-vérifiées est vide. ⚠️ OUVERT D'OFFICE quand il y a quelque chose à retirer : replié, ce qui demande un
 * geste resterait invisible.
 */
function OrphelinsBloc({ rapport, erreur }: { rapport: RapportOrphelins | null; erreur: string | null }) {
  const groupes = rapport ? orphanGroups(rapport) : [];
  const aRetirer = groupes.filter((g) => g.grave).reduce((n, g) => n + g.lignes.length, 0);
  const [open, setOpen] = useState<boolean | null>(null);
  if (erreur) return <div className="dcard orph" style={{ padding: '14px 18px' }}><span style={{ color: 'var(--wa-fg)', fontSize: 'var(--t-dense)' }}>Inventaire des orphelins indisponible — {erreur}</span></div>;
  if (!rapport) return null;
  const partiel = rapport.nonVerifie.length > 0;
  const ouvert = open ?? (aRetirer > 0 || partiel);
  const bilan = aRetirer ? plural(aRetirer, 'élément à retirer', 'éléments à retirer') : partiel ? 'inventaire partiel' : groupes.length ? 'rien à retirer' : 'rien ne survit';
  return (
    <div className="dcard orph">
      <button type="button" className="orph-head" aria-expanded={ouvert} onClick={() => setOpen(!ouvert)}>
        <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>Ce qui survit à un magasin supprimé</span>
        <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{plural(rapport.magasinsConnus, 'magasin', 'magasins')} à l’annuaire</span>
        <span style={{ flex: 1 }} />
        <Pill tone={aRetirer ? 'da' : partiel ? 'wa' : 'ok'}>{bilan}</Pill>
        <span style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--teal-500)', whiteSpace: 'nowrap' }}>{ouvert ? 'Masquer ▴' : 'Détail ▾'}</span>
      </button>
      {ouvert && (
        <div className="orph-body">
          {/* ⚠️ LES SOURCES NON INTERROGÉES D'ABORD : un inventaire partiel qui se présente comme complet est pire qu'absent. */}
          {rapport.nonVerifie.map((m) => (
            <div key={m} style={{ fontSize: 'var(--t-sub)', color: 'var(--wa-fg)', lineHeight: 1.5, paddingTop: 12 }}>Non vérifié — {m}</div>
          ))}
          {groupes.length === 0 && !partiel && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ok-fg)', paddingTop: 14 }}>Rien ne survit : les trois systèmes sont alignés sur l’annuaire.</div>}
          {groupes.map((g) => (
            <div key={g.titre} style={{ paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700, color: g.grave ? 'var(--da-fg)' : 'var(--ink-800)' }}>{g.titre}</span>
                <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{g.lignes.length}</span>
                <Pill tone={g.tone}>{g.verdict}</Pill>
              </div>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.55, marginTop: 5, maxWidth: 760 }}>{g.action}</div>
              {g.cmd && <div className="mono orph-cmd">{g.cmd}</div>}
              <div style={{ marginTop: 8 }}>{g.lignes.map((o) => <LigneOrph key={`${g.titre}-${o.gln}`} o={o} />)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
